<script lang="ts">
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    mdiAccountCheckOutline,
    mdiAccountSwitchOutline,
    mdiCalendarCheckOutline,
    mdiCalendarRemoveOutline,
    mdiCardAccountDetailsOutline,
    mdiCheckCircleOutline,
    mdiFileDownloadOutline,
  } from "@mdi/js"
  import type { HomeDashboardTaskTile } from "../../../../../../../../../../../../api/kehrblatt/home-dashboard"

  const {
    tiles,
    loading = false,
    disabled = false,
    onOpenInbox,
  }: {
    tiles: HomeDashboardTaskTile[]
    loading?: boolean
    disabled?: boolean
    onOpenInbox?: (() => void) | null
  } = $props()

  const iconByName: Record<string, string> = {
    "account-check": mdiAccountCheckOutline,
    "account-switch": mdiAccountSwitchOutline,
    "calendar-check": mdiCalendarCheckOutline,
    "calendar-remove": mdiCalendarRemoveOutline,
    "card-account-details": mdiCardAccountDetailsOutline,
    "file-download": mdiFileDownloadOutline,
  }

  const openInbox = () => {
    if (!disabled && onOpenInbox) {
      onOpenInbox()
    }
  }

  const tileIcon = (tile: HomeDashboardTaskTile) => iconByName[tile.icon] ?? mdiCheckCircleOutline
</script>

<section class="home-dashboard-section">
  <SectionTitle title="Offene Aufgaben" brightBackground={true} small={true} />

  {#if loading}
    <div class="tasks-grid" aria-busy="true">
      {#each [0, 1, 2] as placeholder (placeholder)}
        <div class="task-tile task-tile--loading"></div>
      {/each}
    </div>
  {:else if tiles.length === 0}
    <div class="empty-state">
      <Icon path={mdiCheckCircleOutline} size="1.4rem" />
      <span>Hurra, keine offenen Aufgaben vorhanden.</span>
    </div>
  {:else}
    <div class="tasks-grid">
      {#each tiles as tile (tile.key)}
        <button
          type="button"
          class="task-tile"
          {disabled}
          onclick={openInbox}
          data-testid={`home-dashboard.task.${tile.key}`}
        >
          <span class="task-tile__icon">
            <Icon path={tileIcon(tile)} />
          </span>
          <span class="task-tile__body">
            <span class="task-tile__label">{tile.label}</span>
          </span>
          <span class="task-tile__count">{tile.count}</span>
        </button>
      {/each}
    </div>
  {/if}
</section>

<style lang="less">
  .home-dashboard-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .tasks-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
    gap: 0.75rem;
  }

  .task-tile {
    appearance: none;
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 18%, transparent);
    background: var(--neutral-white);
    color: var(--text-invers-100);
    min-height: 76px;
    padding: 0.9rem;
    display: grid;
    grid-template-columns: 2.4rem 1fr auto;
    align-items: center;
    gap: 0.75rem;
    text-align: left;
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      background 0.15s ease;
  }

  .task-tile:hover:not(:disabled) {
    border-color: var(--primary-100);
    background: color-mix(in srgb, var(--primary-100) 4%, var(--neutral-white));
  }

  .task-tile:focus-visible {
    outline: 3px solid var(--primary-100);
    outline-offset: 3px;
  }

  .task-tile:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .task-tile--loading {
    cursor: default;
    min-height: 86px;
    background: linear-gradient(
      90deg,
      color-mix(in srgb, var(--text-invers-100) 7%, transparent),
      color-mix(in srgb, var(--text-invers-100) 13%, transparent),
      color-mix(in srgb, var(--text-invers-100) 7%, transparent)
    );
  }

  .task-tile__icon {
    width: 2.4rem;
    height: 2.4rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--primary-100) 12%, transparent);
    color: var(--primary-100);
    border: 1px solid color-mix(in srgb, var(--primary-100) 36%, transparent);
  }

  .task-tile__body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .task-tile__label {
    font-weight: 600;
    line-height: 1.4;
    overflow-wrap: anywhere;
    color: var(--text-invers-100);
  }

  .task-tile__count {
    min-width: 2.2rem;
    height: 2.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--primary-100);
    color: var(--neutral-white);
    font-weight: 900;
    font-size: 1rem;
  }

  .empty-state {
    min-height: 92px;
    border: 1px solid color-mix(in srgb, var(--primary-100) 34%, transparent);
    background: color-mix(in srgb, var(--primary-100) 8%, transparent);
    color: var(--text-invers-100);
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 1rem;
    font-weight: 800;
  }

  .empty-state :global(svg) {
    color: var(--primary-100);
  }
</style>
